import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { useToast } from '../../library/useToast'
import { createOfApi } from '../ofApi'
import { ErrorLine, Tabs } from '../settings/kit'
import { AlternativeTab } from './cards/AlternativeTab'
import {
  buildQuery,
  canAlternative,
  isPix,
  LATEST_LIMIT,
  needsPaymentsEmail,
  OF_PAYMENTS,
  OF_VERIFICATION,
  parseQuery,
  refererFor,
  toAlternatives,
  toCards,
  toTxPage,
  toWalletUser,
  txQuery,
  type AlternativeMethod,
  type PageQuery,
  type PaymentCard,
  type TabId,
  type Transaction,
  type WalletUser,
} from './cards/cardsData'
import { CardsTab } from './cards/CardsTab'
import { CardsContext, type CardsCtx } from './cards/context'
import { Alert, CreditCardLogos, OfOnly } from './cards/parts'
import { Loader, TransactionsTab } from './cards/Transactions'
import { LatestTransactions, WalletCredits } from './cards/Wallet'
import type { MorePageProps } from './morePage'
import '../../library/library.css'
import '../ofPages.css'
import '../settings/kit.css'
import './cards/cards.css'

const INTL: Record<string, string> = { en: 'en-US', ru: 'ru-RU', uk: 'uk-UA', es: 'es-ES' }

/** Fenix's two addresses under the card logos (COMPANY_NAME_AND_ADDRESS + the US entity). */
const FENIX = ['Fenix International Limited, 9th Floor, 107 Cheapside, London, EC2V 6DN', 'Fenix Internet LLC, 1000 N.West Street, Suite 1200, Wilmington, Delaware, 19801 USA']

/**
 * Your cards: onlyfans.com/my/payments — the creator's own payment cards,
 * wallet credits, transactions and alternative top-up methods, through
 * OnlyFans' own API. Adding a card, topping up the wallet and the
 * captcha-guarded card actions only work on onlyfans.com; those controls
 * stay disabled with a link there.
 */
export function CardsPage({ canWrite, query, onOpenPage, mobileNav }: MorePageProps) {
  const { t, i18n } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { gateway } = useLibraryDeps()
  const of = useMemo(() => createOfApi(gateway), [gateway])
  const mobile = useIsNarrow()
  const { say, node: toast } = useToast()
  const q = useMemo(() => parseQuery(query), [query])
  const referer = refererFor(q.tab)
  const refererRef = useRef(referer)
  refererRef.current = referer
  const lang = (i18n.language || 'en').slice(0, 2)
  const intl = INTL[lang] ?? 'en-US'

  const [me, setMe] = useState<WalletUser | null>(null)
  const [loadError, setLoadError] = useState('')
  const [nonce, setNonce] = useState(0)
  const [cards, setCardsState] = useState<PaymentCard[] | null>(null)
  const [alternatives, setAltState] = useState<AlternativeMethod[] | null>(null)
  const [paypal, setPaypal] = useState<string[]>([])
  const [hasTransactions, setHasTransactions] = useState(false)
  const [hasTransactionsLoading, setHasTransactionsLoading] = useState(true)
  const [latest, setLatest] = useState<Transaction[]>([])
  const [latestLoading, setLatestLoading] = useState(false)
  const live = useRef(0)

  const fail = useCallback((e: unknown) => say(e instanceof Error && e.message ? e.message : t('error')), [say, t])

  const reloadCards = useCallback(async () => {
    try {
      const raw = await of.get('payments/cards', {}, refererRef.current)
      if (Array.isArray(raw)) setCardsState(toCards(raw))
    } catch {
      setCardsState((c) => c ?? [])
    }
  }, [of])

  const syncPaypal = useCallback(async () => {
    try {
      const r = await of.get<{ paypalStatus?: unknown }>('alternative-payment-methods/paypal', {}, refererRef.current)
      setPaypal(Array.isArray(r?.paypalStatus) ? r.paypalStatus.map(String) : [])
    } catch {
      setPaypal([])
    }
  }, [of])

  /** fetchLatestTransactions: the last five of any type. */
  const reloadLatest = useCallback(async () => {
    setLatestLoading(true)
    try {
      const page = toTxPage(await of.get('payments/all/transactions', txQuery({ limit: LATEST_LIMIT, type: 'all' }), refererRef.current))
      if (page.list.length) setLatest(page.list)
    } catch {
      /* OnlyFans keeps what it had */
    } finally {
      setLatestLoading(false)
    }
  }, [of])

  // What onlyfans.com/my/payments loads: users/me, then cards, alternative
  // methods (credits users only), has-transactions and the PayPal status.
  useEffect(() => {
    const mine = ++live.current
    setLoadError('')
    ;(async () => {
      let user: WalletUser
      try {
        user = toWalletUser(await of.get('users/me', {}, refererRef.current))
      } catch (e) {
        if (mine === live.current) setLoadError(e instanceof Error && e.message ? e.message : t('error'))
        return
      }
      if (mine !== live.current) return
      setMe(user)
      if (canAlternative(user)) {
        of.get('alternative-payment-methods', {}, refererRef.current)
          .then((raw) => mine === live.current && setAltState(toAlternatives(raw)))
          .catch(() => mine === live.current && setAltState([]))
      } else setAltState([])
      setHasTransactionsLoading(true)
      of.get<{ hasTransactions?: boolean }>('payments/all/has-transactions', {}, refererRef.current)
        .then((r) => mine === live.current && setHasTransactions(r?.hasTransactions === true))
        .catch(() => undefined)
        .finally(() => mine === live.current && setHasTransactionsLoading(false))
      void reloadCards()
      void syncPaypal()
    })()
  }, [of, nonce, t, reloadCards, syncPaypal])

  useEffect(() => {
    if (hasTransactions) void reloadLatest()
  }, [hasTransactions, reloadLatest])

  const setQuery = useCallback((next: Partial<PageQuery>) => onOpenPage('cards', buildQuery({ ...q, ...next }), { replace: true }), [q, onOpenPage])
  const openTab = useCallback((tab: TabId) => onOpenPage('cards', buildQuery({ tab }), { replace: true }), [onOpenPage])

  // Alternative methods only for users OnlyFans offers them to (else back to Your cards).
  useEffect(() => {
    if (me && q.tab === 'alternative' && !canAlternative(me)) openTab('cards')
  }, [me, q.tab, openTab])

  const mergeMe = useCallback((patch: Partial<WalletUser>) => setMe((m) => (m ? { ...m, ...patch } : m)), [])

  /** saveUserSetting → users/patchUser: PATCH users/me with the one field. */
  const saveSetting = useCallback(
    async (field: keyof WalletUser, value: boolean | number, showToast = true) => {
      try {
        await of.send('PATCH', 'users/me', refererRef.current, { [field]: value })
      } catch (e) {
        fail(e)
        throw e
      }
      mergeMe({ [field]: value } as Partial<WalletUser>)
      if (showToast) say(t('saved'))
    },
    [of, fail, mergeMe, say, t],
  )

  const ctx: CardsCtx | null = me
    ? {
        of,
        referer,
        canWrite,
        mobile,
        tab: q.tab,
        intl,
        lang,
        say,
        me,
        saveSetting,
        cards,
        setCards: (update) => setCardsState((all) => update(all ?? [])),
        reloadCards,
        alternatives,
        setAlternatives: (update) => setAltState((all) => update(all ?? [])),
        paypal,
        syncPaypal,
        mergeMe,
        hasTransactions,
        hasTransactionsLoading,
        latest,
        latestLoading,
        reloadLatest,
        openTab,
        onOpenPage: (page, pq) => onOpenPage(page, pq),
      }
    : null

  const showAltTab = !!me && canAlternative(me) && alternatives !== null
  const tabs: { id: TabId; label: string }[] = [
    { id: 'cards', label: t('tabs.cards') },
    { id: 'list', label: t('tabs.payments') },
    ...(showAltTab ? [{ id: 'alternative' as const, label: isPix(alternatives ?? []) ? 'Pix' : t('tabs.alternative') }] : []),
  ]

  const head = (
    <header className="ofc-head">
      <button type="button" className="ofc-icon-btn" aria-label={t('back')} title={t('back')} onClick={() => onOpenPage('more')}>
        <FeatherIcon name="arrow-left" size={24} />
      </button>
      <h1 className="ofc-title">{t('title')}</h1>
      {me && (
        <div className="ofc-head-actions">
          {me.ivStatus !== 'approved' && (
            <a className="ofc-flat-btn" href={OF_VERIFICATION} target="_blank" rel="noopener noreferrer">
              {t('verify')}
            </a>
          )}
          {me.canAddCard && q.tab !== 'alternative' && (
            <button type="button" className="ofc-icon-btn" aria-label={t('addCard')} title={`${t('addCard')} — ${t('only.addCard')}`} disabled>
              <FeatherIcon name="credit-card" size={22} />
            </button>
          )}
          {q.tab === 'alternative' && (
            <button type="button" className="ofc-icon-btn" aria-label={t('addAlternative')} title={`${t('addAlternative')} — ${t('only.altAdd')}`} disabled>
              <FeatherIcon name="credit-card" size={22} />
            </button>
          )}
        </div>
      )}
    </header>
  )

  let body
  if (!ctx) {
    body = loadError ? (
      <div className="ofc-pad">
        <ErrorLine onRetry={() => setNonce((n) => n + 1)}>{t('loadFailed', { error: loadError })}</ErrorLine>
      </div>
    ) : (
      <Loader />
    )
  } else if (needsPaymentsEmail(ctx.me)) {
    body = (
      <div className="ofc-pad">
        <Alert variant="danger">{t('only.email')}</Alert>
        <OfOnly text={t('only.emailNote')} href={OF_PAYMENTS} />
      </div>
    )
  } else {
    const tab = q.tab === 'alternative' && !showAltTab ? 'cards' : q.tab
    body = (
      <>
        <div className="ofc-tabs">
          <Tabs value={tab} tabs={tabs} label={t('title')} onChange={openTab} />
        </div>
        {ctx.me.isDeleteInitiated && (
          <div className="ofc-pad">
            <Alert>
              <Trans
                t={t}
                i18nKey="alerts.deleteInitiated"
                components={{ a: <button type="button" className="ofc-link-btn" onClick={() => onOpenPage('settings', 's=account/delete')} /> }}
              />
            </Alert>
          </div>
        )}
        {tab === 'cards' && mobile && (
          <div className="ofc-section">
            <WalletCredits />
          </div>
        )}
        <div className={`ofc-section ${tab === 'list' ? 'is-list' : ''}`}>
          {tab === 'cards' && <CardsTab />}
          {tab === 'list' && <TransactionsTab query={q} onQuery={setQuery} />}
          {tab === 'alternative' && <AlternativeTab />}
        </div>
        {tab === 'cards' && mobile && (
          <div className="ofc-section">
            <LatestTransactions />
          </div>
        )}
        {tab !== 'list' && <CreditCardsWithText />}
      </>
    )
  }

  return (
    <div className={`lib ofp oset ofc ${mobile ? 'lib--m ofc--m' : ''}`}>
      <CardsContext.Provider value={ctx}>
        <div className="ofc-scroll">
          <div className="ofc-columns">
            <main className="ofc-main">
              {head}
              {body}
            </main>
            {!mobile && ctx && !needsPaymentsEmail(ctx.me) && (
              <aside className="ofc-side">
                <WalletCredits />
                <LatestTransactions />
              </aside>
            )}
          </div>
        </div>
      </CardsContext.Provider>
      {toast}
      {mobile && mobileNav}
    </div>
  )
}

/** CreditCardsWithText: the PCI line, the statement note, the brands and Fenix's addresses. */
function CreditCardsWithText() {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  return (
    <div className="ofc-section ofc-legal">
      <p className="ofc-pci">{t('pci')}</p>
      <Alert>{t('statement')}</Alert>
      <CreditCardLogos />
      <p className="ofc-fenix">
        {FENIX[0]}
        <br />
        {FENIX[1]}
      </p>
    </div>
  )
}

